import { useState } from 'react'
import styled from 'styled-components'

import { Json, Result, Url } from '@blade/std-lib'
import { useClipboard } from '@root/shared/hooks'

import type { ClassNameProps } from '@root/shared/react'

type Props = ClassNameProps & {
  value: unknown
  withControls?: boolean
}

export const DebugJson = (props: Props) => {
  const { withControls = true } = props
  const { copyToClipboard } = useClipboard()
  const [isCopied, setCopied] = useState(false)

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[DEBUG JSON]', props.value)
  }
  const content = Url.fromString(props.value as string).pipe(
    Result.map((x) => x.toString()),
    Result.orElse(() => Json.stringify(props.value, undefined, 2)),
    Result.getOrElse((error) =>
      error.cause instanceof Error
        ? `${error.name}: ${error.message}`
        : 'Unknown error during Json.stringify',
    ),
  )

  const handleCopy: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.stopPropagation()
    e.preventDefault()

    if (isCopied) {
      return
    }

    const isDone = await copyToClipboard(content)

    if (isDone) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1000)
    }
  }

  return import.meta.env.DEV ? (
    <Root className={props.className}>
      {withControls ? (
        <>
          <CopyButton type="button" onClick={handleCopy}>
            {isCopied ? 'done' : 'copy'}
          </CopyButton>
        </>
      ) : undefined}
      <Content>{content}</Content>
    </Root>
  ) : undefined
}

const CopyButton = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 11px;
  padding: 2px 4px;
  background: black;
  color: var(--yellow-11);
  line-height: normal;
  font-weight: 700;
  font-family: monospace;

  &:hover {
    background: var(--yellow-11);
    color: black;
  }
`

const Root = styled.div`
  overflow: auto;
  position: relative;
  padding: 3px;
  background: repeating-linear-gradient(
    -45deg,
    var(--yellow-11),
    var(--yellow-11) 5px,
    black 5px,
    black 10px
  );

  & * {
    user-select: auto;
    &::selection {
      background: var(--down-9);
    }
  }
`
const Content = styled.pre`
  margin: 0;
  padding: 6px;
  min-width: 100%;
  width: max-content;

  background: black;
  color: var(--yellow-11);

  font-size: 12px;
  line-height: normal;
  font-weight: 700;
  font-family: monospace;
`
